
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { CheckCircle, PlusCircle, Dumbbell, Clock } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { AIExercisePlanGenerator } from "@/components/exercise-plans/ai-exercise-plan-generator"
import { useGenerateExercisePlanWithAI } from "@/api/exercise-plans"
import { useToast } from "@/hooks/use-toast"

const defaultPlan = {
  name: "My Exercise Plan",
  type: "Strength",
  difficulty: "Intermediate",
  duration: 45,
  exercises: [
    { name: "Push-ups", sets: 3, reps: "12", description: "Standard push-ups", rest: "60s" },
    { name: "Bodyweight Squats", sets: 3, reps: "15", description: "Bodyweight squats", rest: "60s" },
    { name: "Dumbbell Rows", sets: 3, reps: "10", description: "Single-arm dumbbell rows", rest: "60s" },
    { name: "Plank", sets: 3, reps: "30s", description: "Hold plank position", rest: "30s" },
    { name: "Dumbbell Curls", sets: 3, reps: "12", description: "Bicep curls", rest: "60s" },
  ],
}

export function UserExercisePlans() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [activeTab, setActiveTab] = useState("current")
  const [showGenerateDialog, setShowGenerateDialog] = useState(false)
  const [currentPlan] = useState(defaultPlan)
  const generateExercisePlanWithAI = useGenerateExercisePlanWithAI()

  // Mock data for completed exercises
  const [completedExercises, setCompletedExercises] = useState<Record<string, boolean>>({
    "Push-ups": true,
    "Bodyweight Squats": true,
    "Dumbbell Rows": false,
    Plank: false,
    "Dumbbell Curls": false,
  })

  const toggleExerciseCompletion = (exerciseName: string) => {
    setCompletedExercises((prev) => ({
      ...prev,
      [exerciseName]: !prev[exerciseName],
    }))
  }

  const handleAIGenerate = async (prompt: string) => {
    try {
      const result = await generateExercisePlanWithAI.mutateAsync({ prompt })

      // For user view, we might want to save the generated plan or display it
      console.log("Generated exercise plan:", result)

      toast({
        title: t("ai.planGenerated"),
        description: t("ai.reviewAndEdit"),
      })

      setShowGenerateDialog(false)
    } catch (error) {
      console.error("Error generating exercise plan with AI:", error)
      toast({
        title: t("common.error"),
        description: t("ai.generationFailed"),
        variant: "destructive",
      })
    }
  }

  // Calculate progress
  const totalExercises = currentPlan.exercises.length
  const completedCount = Object.values(completedExercises).filter(Boolean).length
  const progressPercentage = Math.round((completedCount / totalExercises) * 100)

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{t("userView.exercise.title")}</h1>
        <Button onClick={() => setShowGenerateDialog(true)}>
          <PlusCircle className="h-4 w-4 mr-2" />
          {t("userView.exercise.generatePlan")}
        </Button>
      </div>

      {/* Current plan progress */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle>{currentPlan.name}</CardTitle>
          <CardDescription>
            {t("userView.exercise.difficulty")}: {currentPlan.difficulty}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>{t("userView.exercise.progress")}</span>
              <span>{progressPercentage}%</span>
            </div>
            <Progress value={progressPercentage} />
          </div>

          <div className="flex flex-wrap gap-2 text-sm">
            <div className="flex items-center bg-muted px-2 py-1 rounded-md">
              <Dumbbell className="h-4 w-4 mr-1" />
              <span>{currentPlan.type}</span>
            </div>
            <div className="flex items-center bg-muted px-2 py-1 rounded-md">
              <Clock className="h-4 w-4 mr-1" />
              <span>
                {currentPlan.duration} {t("userView.exercise.minutes")}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Exercise list */}
      <Card>
        <CardHeader>
          <CardTitle>{t("userView.exercise.todaysWorkout")}</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            {currentPlan.exercises.map((exercise, index) => (
              <AccordionItem key={index} value={`exercise-${index}`}>
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center justify-between w-full pr-4">
                    <div className="flex items-center">
                      <Button
                        variant={completedExercises[exercise.name] ? "default" : "outline"}
                        size="icon"
                        className="h-6 w-6 mr-3 rounded-full"
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleExerciseCompletion(exercise.name)
                        }}
                      >
                        <CheckCircle className="h-4 w-4" />
                        <span className="sr-only">
                          {completedExercises[exercise.name] ? "Mark as incomplete" : "Mark as complete"}
                        </span>
                      </Button>
                      <span className={completedExercises[exercise.name] ? "line-through text-muted-foreground" : ""}>
                        {exercise.name}
                      </span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {exercise.sets} × {exercise.reps}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="pl-9 space-y-2">
                    <p className="text-sm">{exercise.description}</p>
                    <p className="text-sm text-muted-foreground">
                      {t("userView.exercise.rest")}: {exercise.rest}
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
        <CardFooter>
          <Button className="w-full">{t("userView.exercise.completeWorkout")}</Button>
        </CardFooter>
      </Card>

      {/* Generate plan dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t("userView.exercise.generatePlan")}</DialogTitle>
            <DialogDescription>{t("userView.exercise.generatePlanDescription")}</DialogDescription>
          </DialogHeader>

          <AIExercisePlanGenerator
            title={t("userView.exercise.customizeYourPlan")}
            description={t("ai.exercisePlanDescription")}
            placeholder={t("ai.exercisePlanPlaceholder")}
            onGenerate={handleAIGenerate}
            isGenerating={generateExercisePlanWithAI.isPending}
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
              {t("common.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
